import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * @typedef {import('class-variance-authority').VariantProps<typeof buttonVariants>} ButtonVariantProps
 * @typedef {React.ButtonHTMLAttributes<HTMLButtonElement> & ButtonVariantProps} ButtonProps
 */

const buttonVariants = cva(
  'inline-flex gap-4 items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: 'bg-mavielo-200 text-white hover:bg-mavielo-200/80',
        destructive: 'bg-red-500 text-white hover:bg-red-600',
        outline: 'border border-gray-200 bg-white hover:bg-gray-200',
        secondary: 'bg-gray-50 text-gray-900 hover:bg-gray-200',
        ghost: 'hover:bg-gray-200 text-gray-600 hover:text-gray-900',
        link: 'text-mavielo-200 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 py-2 px-4',
        sm: 'h-9 px-3 rounded-lg',
        lg: 'h-11 px-8 rounded-lg',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

/**
 * Renders a button with pre-defined styles and variants.
 * @param {ButtonProps} props
 */
function ButtonComponent({ className, variant, size, ...props }, ref) {
  return (
    <button
      className={twMerge(buttonVariants({ variant, size }), className)}
      ref={ref}
      {...props}
    />
  );
}

const Button = forwardRef(ButtonComponent);
Button.displayName = 'Button';

export { Button };
